import type { MouseEvent, ReactNode } from "react"

import type { ExtraDataItem } from "../hooks/use-citation"
import type { Document } from "@/api/models"

import { Book, Braces, Eye, FileDown, FileText } from "@/components/icons"
import { cn } from "@/lib/utils"

import { buttonVariants } from "../../../components/ui/button-variants"
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../components/ui/tooltip"

type DocumentCitationItemProps = {
  citation: string
  category?: string | null
  extraData: ExtraDataItem[] | null
  formatCitationDisplay: (
    citation: string,
    librariesAndDocuments?: [string, Document][]
  ) => {
    fileNameWithPage: string
    fileNameWithoutPage: string
    pageNumber: number | null
  }
  handleDownloadClicked: (path: string, downloadFileName?: string) => void
  index: number
  pathWithPage: string
  /** Real filename for `pathWithPage` when its URL has none to recover. */
  downloadFileName?: string
  setExtraData: (data: ExtraDataItem[] | null) => void
  setOpenMetadataDialog: (open: boolean) => void
  citationSection: string
  chunkText?: string | null
  /** When set (e.g. library section), passed through so previews resolve originals and titles. */
  documentsList?: [string, Document][]
  onViewDocument: (
    citation: string,
    citationSection: string,
    options?: { chunkText?: string; documents?: [string, Document][] }
  ) => void
  t: (key: string) => string
  disablePreview?: boolean
  disableMetadata?: boolean
  disableDownload?: boolean
  isHighlighted?: boolean
  messageIndex?: number
  isLibraryDocument?: boolean
  onNavigateToInline?: (index: number) => void
}

function IconButton({
  onClick,
  children,
  tooltip,
}: {
  onClick: (e: MouseEvent<HTMLButtonElement>) => void
  children: ReactNode
  tooltip: string
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        className={cn(
          buttonVariants({
            size: "icon",
            variant: "ghost",
          }),
          "h-6 w-6 p-0"
        )}
        onClick={onClick}
      >
        {children}
      </TooltipTrigger>
      <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
        <p>{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  )
}

export function DocumentCitationItem({
  citation,
  category,
  extraData,
  formatCitationDisplay,
  handleDownloadClicked,
  index,
  pathWithPage,
  downloadFileName,
  setExtraData,
  setOpenMetadataDialog,
  citationSection,
  chunkText,
  documentsList,
  onViewDocument,
  t,
  disablePreview = false,
  disableMetadata = false,
  disableDownload = false,
  isHighlighted = false,
  messageIndex,
  isLibraryDocument = false,
  onNavigateToInline,
}: DocumentCitationItemProps) {
  const { fileNameWithoutPage, pageNumber } = formatCitationDisplay(citation, documentsList)

  const colonIndex = fileNameWithoutPage.indexOf(": ")
  const libraryName = colonIndex !== -1 ? fileNameWithoutPage.substring(0, colonIndex) : ""
  const title =
    colonIndex !== -1 ? fileNameWithoutPage.substring(colonIndex + 2) : fileNameWithoutPage
  const displayCategory =
    typeof category === "string" &&
    category.trim() &&
    !["web", "file", "uploaded", "library"].includes(category.trim().toLowerCase())
      ? category.trim()
      : null
  const librarySubtitle = displayCategory ? `${libraryName} · ${displayCategory}` : libraryName

  const handleViewDocument = () => {
    onViewDocument(citation, citationSection, {
      chunkText: chunkText ?? undefined,
      ...(documentsList ? { documents: documentsList } : {}),
    })
  }

  const hasMetadata = !disableMetadata && Array.isArray(extraData) && extraData.length > 0
  const isRowInteractive = typeof onNavigateToInline === "function"

  const contentClassName = "flex min-w-0 flex-1 items-center gap-2 text-left"
  const content = (
    <>
      <span className="text-muted-foreground ml-1 shrink-0 text-xs font-medium">{index + 1}.</span>
      {isLibraryDocument ? (
        <Book className="text-muted-foreground size-4 shrink-0" />
      ) : (
        <FileText className="text-muted-foreground size-4 shrink-0" />
      )}
      <span className="block min-w-0 flex-1 space-y-1">
        <span className="flex items-center gap-2">
          <span className="block truncate text-xs font-medium" title={title}>
            {title}
          </span>
          {pageNumber && (
            <span className="text-muted-foreground block shrink-0 text-xs">
              {t("answer.page")} {pageNumber}
            </span>
          )}
        </span>
        <span className="text-muted-foreground block truncate text-xs" title={librarySubtitle}>
          {librarySubtitle}
        </span>
      </span>
    </>
  )

  return (
    <div
      data-citation-index={index}
      data-message-index={messageIndex}
      role="presentation"
      className={cn(
        "border-border/50 bg-muted/30 hover:border-primary/30 flex items-center justify-between gap-2 rounded-md border p-2 text-sm transition-colors",
        isRowInteractive && "cursor-pointer",
        isHighlighted && "animate-[highlight_2s_ease-in-out]"
      )}
      // Mouse convenience for the whole row; keyboard users activate the inner button.
      onClick={() => {
        onNavigateToInline?.(index)
      }}
    >
      {isRowInteractive ? (
        <button
          type="button"
          className={cn(
            contentClassName,
            "focus-visible:ring-ring cursor-pointer rounded-sm focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
          )}
          onClick={(e) => {
            // Handled here; stop the bubble so the row's mouse handler doesn't fire twice.
            e.stopPropagation()
            onNavigateToInline(index)
          }}
        >
          {content}
        </button>
      ) : (
        <div className={contentClassName}>{content}</div>
      )}
      <section className="flex flex-row items-center gap-1">
        {hasMetadata && (
          <IconButton
            tooltip={t("answer.metadata")}
            onClick={(e) => {
              e.stopPropagation()
              setExtraData(extraData)
              setOpenMetadataDialog(true)
            }}
          >
            <Braces className="size-4" />
          </IconButton>
        )}
        {!disableDownload && (
          <IconButton
            tooltip={t("answer.download_document")}
            onClick={(e) => {
              e.stopPropagation()
              handleDownloadClicked(pathWithPage, downloadFileName)
            }}
          >
            <FileDown className="size-4" />
          </IconButton>
        )}
        {!disablePreview && (
          <IconButton
            tooltip={t("answer.show_document")}
            onClick={(e) => {
              e.stopPropagation()
              handleViewDocument()
            }}
          >
            <Eye className="size-4" />
          </IconButton>
        )}
      </section>
    </div>
  )
}
